import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';

import {
  aiScheduledActionsAtom,
  aiSchedulerProcessingDateAtom,
  aiSchedulerStatusTextAtom,
  aiSchedulerWeekCompleteAtom,
  calendarViewSwitcherAtom,
  generalSnackBarAtom,
  homeViewSwitcherAtom,
  isRunningAISchedulerAtom,
  selectedDateAtom,
} from '../recoil';
import {
  applyWeekPlanDayComplete,
  clearPersistedJob,
  extractWeekPlanStatusText,
  clearPersistedJobIfMatches,
  getPersistedJob,
  getWeekPlanDates,
  readWeeklyPlanCache,
  writeWeeklyPlanCache,
  WeeklyPlanJobContext,
  WeeklyPlanStreamResult,
  WEEKLY_PLAN_JOB_TYPE,
} from '../helpers';
import { JobNotFoundError } from '../api/jobs';
import { useJobPolling } from './useJobPolling';

export const getPersistedWeeklyPlanJob = () =>
  getPersistedJob<WeeklyPlanJobContext>(WEEKLY_PLAN_JOB_TYPE);

const WEEKLY_PLAN_FAILED_MESSAGE = "Couldn't finish planning your week.";

interface UseWeeklyPlanJobRecoveryParams {
  /** Called once, synchronously, whenever a run (in-flight or just finished)
   * is found to resume — lets the caller reopen whatever UI shows it (the
   * recovered atoms alone don't bring the scheduler chat panel back). */
  onResume?: () => void;
}

/**
 * Resumes a plan-my-week job that was still running when the tab reloaded or
 * closed. Mounted once at app root (AssistantChatProvider) — reads the job_id
 * persisted by useRunAIScheduler's runAIWeekPlanStream and, if one exists,
 * polls GET /api/v1/jobs/{job_id}, folding each persisted `day_complete`
 * through the same applyWeekPlanDayComplete the live stream uses.
 *
 * If there is no job in flight, falls back to the last *completed* week plan
 * (written by runAIWeekPlanStream once it finishes) — the pending-job record
 * is gone by then, so without this a reload right after a run finishes shows
 * an empty calendar instead of the plan that was just generated.
 */
export const useWeeklyPlanJobRecovery = ({
  onResume,
}: UseWeeklyPlanJobRecoveryParams = {}): void => {
  const setAiScheduledActions = useSetRecoilState(aiScheduledActionsAtom);
  const setIsRunning = useSetRecoilState(isRunningAISchedulerAtom);
  const setStatusText = useSetRecoilState(aiSchedulerStatusTextAtom);
  const setProcessingDates = useSetRecoilState(aiSchedulerProcessingDateAtom);
  const setIsWeekComplete = useSetRecoilState(aiSchedulerWeekCompleteAtom);
  const setCalendarView = useSetRecoilState(calendarViewSwitcherAtom);
  const setHomeViewSwitcher = useSetRecoilState(homeViewSwitcherAtom);
  const setSelectedDate = useSetRecoilState(selectedDateAtom);
  const setSnackbar = useSetRecoilState(generalSnackBarAtom);

  const contextRef = useRef<WeeklyPlanJobContext | null>(null);
  const resultRef = useRef<WeeklyPlanStreamResult>({ schedules: [], summary: null });
  const [resumeJobId, setResumeJobId] = useState<string | null>(null);

  useEffect(() => {
    const persisted = getPersistedWeeklyPlanJob();
    if (persisted) {
      const { weekStart, numDays } = persisted.context;
      contextRef.current = persisted.context;
      resultRef.current = { schedules: [], summary: null };

      setHomeViewSwitcher('Calendar');
      setCalendarView('week');
      setSelectedDate(dayjs(weekStart).startOf('day'));
      setAiScheduledActions([]);
      setStatusText([]);
      setProcessingDates(getWeekPlanDates(weekStart, numDays));
      setIsWeekComplete(false);
      setIsRunning(true);
      setResumeJobId(persisted.jobId);
      onResume?.();
      return;
    }

    const cached = readWeeklyPlanCache();
    if (!cached) {
      return;
    }
    setHomeViewSwitcher('Calendar');
    setCalendarView('week');
    setSelectedDate(dayjs(cached.weekStart).startOf('day'));
    setAiScheduledActions(cached.schedules);
    setStatusText([]);
    setProcessingDates([]);
    setIsWeekComplete(true);
    setIsRunning(false);
    onResume?.();
    // Mount-only: one fixed job/result to pick up per app load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    if (resumeJobId) {
      clearPersistedJobIfMatches(WEEKLY_PLAN_JOB_TYPE, resumeJobId);
    }
    setStatusText([]);
    setProcessingDates([]);
    setIsWeekComplete(true);
    setIsRunning(false);
    setResumeJobId(null);
  };

  useJobPolling({
    jobId: resumeJobId,
    onEvent: (event) => {
      const context = contextRef.current;
      if (!context) {
        return;
      }
      const statusText = extractWeekPlanStatusText(event.type, event.data);
      if (statusText) {
        setStatusText((prev) => [...prev, statusText]);
        return;
      }
      if (!event.data || typeof event.data !== 'object') {
        return;
      }
      const data = event.data as { date?: string; events?: unknown; summary?: string | null };

      if (event.type === 'day_complete') {
        resultRef.current = applyWeekPlanDayComplete(
          resultRef.current,
          data,
          context.weekStart,
          context.tasks
        );
        setAiScheduledActions(resultRef.current.schedules);
        if (data.date) {
          setProcessingDates((prev) => prev.filter((date) => date !== data.date));
        }
      } else if (event.type === 'week_complete' && data.summary) {
        resultRef.current = { ...resultRef.current, summary: data.summary };
      }
    },
    onDone: () => {
      // The live stream caches its own result in useRunAIScheduler; a job
      // resumed here polls the jobs endpoint directly instead, so it has to
      // write the same cache itself once it reaches this terminal state.
      const context = contextRef.current;
      if (context && resultRef.current.schedules.length > 0) {
        writeWeeklyPlanCache({
          weekStart: context.weekStart,
          numDays: context.numDays,
          schedules: resultRef.current.schedules,
          summary: resultRef.current.summary,
          completedAt: Date.now(),
        });
      }
      finish();
    },
    onCancelled: finish,
    onError: (_response, error) => {
      // A 404 means the job expired or never reached science; there is nothing
      // to report, so only a genuine failure with nothing planned raises a message.
      const hadResults = resultRef.current.schedules.length > 0;
      clearPersistedJob(WEEKLY_PLAN_JOB_TYPE);
      finish();
      if (!hadResults && !(error instanceof JobNotFoundError)) {
        setSnackbar(WEEKLY_PLAN_FAILED_MESSAGE);
      }
    },
  });
};
